import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import useCopilotValue, { updateCopilotValue } from "../hooks/useCopilotValue";
import CopilotDock from "./CopilotDock";
import CopilotPanel, {
  EXPANDED_PANEL_HEIGHT,
  EXPANDED_PANEL_WIDTH,
  PANEL_HEIGHT,
  PANEL_WIDTH,
} from "./CopilotPanel";

type Pos = { left: number; top: number };

const DEFAULT_LEFT = 80;
const DEFAULT_BOTTOM = 14;

const getDefaultPos = (): Pos => ({
  left: DEFAULT_LEFT,
  top: window.innerHeight - DEFAULT_BOTTOM - PANEL_HEIGHT,
});

// Reposition so the center of the panel stays fixed across size changes.
function centerPreserve(cur: Pos, fromW: number, fromH: number, toW: number, toH: number): Pos {
  const cx = cur.left + fromW / 2;
  const cy = cur.top + fromH / 2;
  return {
    left: Math.max(0, Math.min(window.innerWidth - toW, cx - toW / 2)),
    top: Math.max(0, Math.min(window.innerHeight - toH, cy - toH / 2)),
  };
}

const CopilotRoot = () => {
  const { isPanelOpen, isHidden } = useCopilotValue();

  // Persists position across open/close cycles. Always stored as small-size top-left.
  const savedPanelPos = useRef<Pos | null>(null);
  // Kept in sync with openPos state so drag callbacks always read the latest value.
  const openPosRef = useRef<Pos | null>(null);
  const [openPos, setOpenPos] = useState<Pos | null>(null);
  // Prevents backdrop click from closing the panel right after a drag ends.
  const isDraggingRef = useRef(false);

  const [isExpanded, setIsExpanded] = useState(false);
  // Ref mirrors isExpanded so the drag handler can read the latest value
  // without being recreated on every expand toggle.
  const isExpandedRef = useRef(false);
  // Saves the exact position before expanding so collapsing restores it precisely.
  // Cleared when the user drags while expanded so collapse uses center-preserve instead.
  const preExpandPosRef = useRef<Pos | null>(null);

  const applyPos = useCallback((pos: Pos | null) => {
    openPosRef.current = pos;
    setOpenPos(pos);
  }, []);

  useEffect(() => {
    if (isPanelOpen) {
      applyPos(savedPanelPos.current ?? getDefaultPos());
    } else {
      // Normalize to small-size position before saving so reopening always
      // starts in collapsed state at the correct location.
      if (openPosRef.current) {
        savedPanelPos.current = isExpandedRef.current
          ? centerPreserve(
              openPosRef.current,
              EXPANDED_PANEL_WIDTH,
              EXPANDED_PANEL_HEIGHT,
              PANEL_WIDTH,
              PANEL_HEIGHT
            )
          : openPosRef.current;
      }
      applyPos(null);
      setIsExpanded(false);
      isExpandedRef.current = false;
      preExpandPosRef.current = null;
    }
  }, [isPanelOpen, applyPos]);

  const onToggleExpand = useCallback(() => {
    const next = !isExpandedRef.current;
    isExpandedRef.current = next;

    // Compute the new position synchronously so both isExpanded and openPos
    // change in the same React batch — prevents a one-frame jerk where the
    // panel renders at the new size but the old position.
    if (openPosRef.current) {
      if (next) {
        // Expanding: save exact current position so collapsing without drag restores it precisely.
        preExpandPosRef.current = openPosRef.current;
        applyPos(centerPreserve(openPosRef.current, PANEL_WIDTH, PANEL_HEIGHT, EXPANDED_PANEL_WIDTH, EXPANDED_PANEL_HEIGHT));
      } else if (preExpandPosRef.current) {
        // Collapsing after no drag: restore the exact pre-expand position.
        applyPos(preExpandPosRef.current);
        preExpandPosRef.current = null;
      } else {
        // Collapsing after drag while expanded: center-preserve from wherever user dragged to.
        applyPos(centerPreserve(openPosRef.current, EXPANDED_PANEL_WIDTH, EXPANDED_PANEL_HEIGHT, PANEL_WIDTH, PANEL_HEIGHT));
      }
    }

    setIsExpanded(next);
  }, [applyPos]);

  // Re-clamp on window resize so the panel never becomes unreachable.
  useEffect(() => {
    let rafId: number;
    const onResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (!openPosRef.current) return;
        const pw = isExpandedRef.current ? EXPANDED_PANEL_WIDTH : PANEL_WIDTH;
        const ph = isExpandedRef.current ? EXPANDED_PANEL_HEIGHT : PANEL_HEIGHT;
        const cur = openPosRef.current;
        applyPos({
          left: Math.max(0, Math.min(window.innerWidth - pw, cur.left)),
          top: Math.max(0, Math.min(window.innerHeight - ph, cur.top)),
        });
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
    };
  }, [applyPos]);

  const onOutsideClick = useCallback(() => {
    if (isDraggingRef.current) return;
    updateCopilotValue({ isPanelOpen: false });
  }, []);

  const onDragHandlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      // Capture the pointer so pointermove/pointerup fire even when the cursor
      // leaves the browser window — prevents stuck isDraggingRef and listener leaks.
      (e.target as Element).setPointerCapture(e.pointerId);

      const startX = e.clientX;
      const startY = e.clientY;
      const initLeft = openPosRef.current?.left ?? DEFAULT_LEFT;
      const initTop = openPosRef.current?.top ?? getDefaultPos().top;

      const onPointerMove = (evt: PointerEvent) => {
        const dx = evt.clientX - startX;
        const dy = evt.clientY - startY;

        if (!isDraggingRef.current && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
          isDraggingRef.current = true;
          preExpandPosRef.current = null;
          document.body.style.cursor = "grabbing";
          document.body.style.userSelect = "none";
        }

        if (!isDraggingRef.current) return;

        const pw = isExpandedRef.current ? EXPANDED_PANEL_WIDTH : PANEL_WIDTH;
        const ph = isExpandedRef.current ? EXPANDED_PANEL_HEIGHT : PANEL_HEIGHT;
        applyPos({
          left: Math.max(0, Math.min(window.innerWidth - pw, initLeft + dx)),
          top: Math.max(0, Math.min(window.innerHeight - ph, initTop + dy)),
        });
      };

      const onPointerUp = () => {
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        // Allow the backdrop click handler to check isDraggingRef before we clear it.
        setTimeout(() => {
          isDraggingRef.current = false;
        }, 0);
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [applyPos]
  );

  // Compute position synchronously so there is no one-frame flash on open.
  const effectivePos: Pos | null = isPanelOpen
    ? (openPos ?? savedPanelPos.current ?? getDefaultPos())
    : null;

  // Close an already-open panel when hidden, so it can't silently reappear
  // (with its full-screen backdrop) the moment the page unhides it later.
  useEffect(() => {
    if (isHidden && isPanelOpen) {
      updateCopilotValue({ isPanelOpen: false });
    }
  }, [isHidden, isPanelOpen]);

  // Must come after all hooks above.
  if (isHidden) return null;

  return (
    <>
      {isPanelOpen && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1249}
          onClick={onOutsideClick}
          sx={{
            cursor: "default",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            animation: "fadeIn 225ms ease-in",
            "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
          }}
        />
      )}
      <Box
        sx={
          effectivePos
            ? {
                position: "fixed",
                top: effectivePos.top,
                left: effectivePos.left,
                zIndex: 1250,
              }
            : {
                position: "fixed",
                bottom: DEFAULT_BOTTOM,
                left: DEFAULT_LEFT,
                zIndex: 1250,
              }
        }
      >
        {isPanelOpen ? (
          <CopilotPanel
            onDragHandlePointerDown={onDragHandlePointerDown}
            isExpanded={isExpanded}
            onToggleExpand={onToggleExpand}
          />
        ) : (
          <CopilotDock />
        )}
      </Box>
    </>
  );
};

export default memo(CopilotRoot);
